import React from 'react';

interface ProcessSynthesisLoadingProps {
  isAnalyzing: boolean;
  analysisProgress: {
    progress: number;
    completed: number;
    failed: number;
    pending: number;
    analyzing: number;
    total: number;
  } | null;
}

const ProcessSynthesisLoading: React.FC<ProcessSynthesisLoadingProps> = ({
  isAnalyzing,
  analysisProgress
}) => {
  // Determine loading state and message
  const analysisHasRun = analysisProgress && (analysisProgress.completed > 0 || analysisProgress.failed > 0);
  const isWaitingForSynthesisAfterAnalysis = !isAnalyzing && analysisHasRun;

  let message = 'Process synthesis will be available after analysis is complete.';
  let showSpinner = false;

  if (isAnalyzing) {
    message = 'Analysis is in progress. Process synthesis will be generated upon completion.';
    showSpinner = true;
  } else if (isWaitingForSynthesisAfterAnalysis) {
    message = 'Finalizing analysis and generating process synthesis...';
    showSpinner = true;
  }

  return (
    <div className="space-y-6">
      {/* End-to-End Process Flow Section */}
      <div className="bg-light-purple rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 00-2-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2H9z" />
            </svg>
            End-to-End Process Flow
          </h3>
          <button className="text-electric-blue hover:text-electric-blue-dark transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
        </div>

        <div className="flex flex-col items-center justify-center py-12">
          {showSpinner && (
            <div className="relative mb-6">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200"></div>
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-electric-blue border-t-transparent absolute top-0 left-0"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-6 h-6 bg-electric-blue rounded-full animate-pulse"></div>
              </div>
            </div>
          )}
          
          <p className="text-gray-600 text-center max-w-md">{message}</p>
          
          {isAnalyzing && analysisProgress && (
            <div className="mt-4 w-full max-w-md">
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div 
                  className="bg-gradient-to-r from-electric-blue to-purple-600 h-2 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${analysisProgress.progress}%` }}
                ></div>
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>{analysisProgress.completed} completed</span>
                <span>{analysisProgress.total} total documents</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProcessSynthesisLoading;